import { ITelemetryNamespaceLogger, OdspResourceTokenRequestOptions, TokenProvider } from '@fluidx/office-fluid-types';
import { ITelemetryBaseLogger } from '@fluidframework/common-definitions';
import { constantBackoff, RetryPolicy, whitelist } from '../fetch/fetchWithRetry';
import { getOrigin } from '../fetch/getOrigin';
import { AuthorizedFetchProps, authorizedFetchWithRetry, instrumentedAuthorizedFetchWithRetry } from '../fetch';
import { getOdspAndGraphRequestIdsFromResponse } from '../fetch/getOdspAndGraphRequestIdsFromResponse';
import { IFluidResolvedUrl, IResolvedUrl } from '@fluidframework/driver-definitions';
import { IOdspResolvedUrl } from '@fluidframework/odsp-driver-definitions';

const vroomRetryPolicy: RetryPolicy<Response> = {
  maxRetries: 2,
  backoffFn: constantBackoff(500),
  filter: whitelist([408, 409, 429])
};

/**
 * This represents a lite version of file item containing only the name, webUrl and webDavUrl properties
 */
export interface FileItemLite {
  webUrl: string;
  webDavUrl: string;
  name: string;
}

// Store details of the requested items for the lifetime of the app
const fileItemLiteCache: { [itemKey: string]: Promise<FileItemLite | undefined> } = {};

/**
 * Gets file item information
 * @param tokenProvider Token provider than can supply access token needed to execute operation
 * @param siteUrl Url of the site where file resides
 * @param driveId Drive id where file resides
 * @param fileId File id
 * @param logger used to log results of operation, including any error
 * @param tenantId tenant id is used to designate AAD tenant where file resides. This can be specified in shape of GUID or
 * domain hostname. If not specified then tenant will be determined based on given siteUrl..
 * @returns Object containing name, webUrl and webDavUrl properties.
 * - name represents file name
 * - webUrl represents file url, this is url that can be used for direct loading of file on web. Primarily used
 * as canonical url for file residing in ODC
 * - webDavUrl represents file url in WebDAV standard, this url includes file path. This is needed for APIs
 * powering MRU and Share functionality.
 * Sample webUrl for SharePoint: "https://contoso-my.sharepoint.com/personal/johnd_contoso_com/_layouts/15/Doc.aspx?sourcedoc=%7B592D8007-BAAF-498C-8288-0DBC52870406%7D&file=test.fluid&action=default"
 * Sample webUrl for ODC: "https://1drv.ms/u/s!AOMVqPBIV2EghbpA"
 * Sample webDavUrl for SharePoint: "https://contoso-my.sharepoint.com/personal/johnd_contoso_com/Fluid%20Preview%20Docs/test.fluid"
 * Sample webDavUrl for ODC: "https://d.docs.live.net/20615748f0a815e3/Fluid%20Preview%20Docs/test.fluid"
 */
export function getFileItemLite(
  tokenProvider: TokenProvider,
  siteUrl: string,
  driveId: string,
  itemId: string,
  logger?: ITelemetryBaseLogger,
  tenantId?: string
): Promise<FileItemLite | undefined> {
  const siteOrigin = getOrigin(siteUrl);
  const cacheKey = `${siteOrigin}_${driveId}_${itemId}`;
  if (!fileItemLiteCache[cacheKey]) {
    fileItemLiteCache[cacheKey] = new Promise<FileItemLite | undefined>(async (resolve) => {
      const isConsumer = tokenProvider.identityType === 'Consumer';
      const url = `${siteOrigin}/${
        isConsumer ? '' : '_api/'
      }v2.0/drives/${driveId}/items/${itemId}?select=webUrl,webDavUrl,name`;
      const authorizedFetchProps: AuthorizedFetchProps = {
        getToken: (options) => {
          const odspResourceOptions: OdspResourceTokenRequestOptions = {
            ...options,
            siteUrl,
            driveId,
            itemId
          };
          return tokenProvider.getToken(
            [isConsumer ? 'onedrive.readwrite' : `${siteOrigin}/files.readwrite.all`],
            odspResourceOptions
          );
        },
        url,
        tenantId,
        requestInit: { method: 'GET' },
        retryPolicy: vroomRetryPolicy
      };
      const fetchResponse = logger
        ? instrumentedAuthorizedFetchWithRetry({
            ...authorizedFetchProps,
            logger,
            nameForLogging: 'GetFileItemLite',
            doNotLogOnSuccess: true,
            getAdditionalProps: async (response, _isFinalAttempt) => getOdspAndGraphRequestIdsFromResponse(response)
          })
        : authorizedFetchWithRetry(authorizedFetchProps);

      let response: Response | undefined;
      try {
        response = (await fetchResponse).result;
      } catch {}

      // Cache only if we got a response and the response was a 200 (success) or 404 (NotFound)
      if (!response || (response.status !== 200 && response.status !== 404)) {
        delete fileItemLiteCache[cacheKey];
        resolve(undefined);
      } else {
        try {
          const result = (await response.json()) as FileItemLite;
          resolve(result);
        } catch {
          delete fileItemLiteCache[cacheKey];
          resolve(undefined);
        }
      }
    });
  }

  return fileItemLiteCache[cacheKey];
}

/**
 * Returns location of an odsp file given an Odsp resolved url
 * @param resolvedUrl Maybe IOdspResolvedUrl
 * @param tokenProvider Returns tokens with the given set of scopes
 * @param logger Namespace logger
 * @param tenantId tenant id is used to designate AAD tenant where file resides. This can be specified in shape of GUID or
 * domain hostname. If not specified then item is presumed to reside in the tenancy of currently signed in user.
 */
export const getOdspFileLocation = async (
  resolvedUrl: IResolvedUrl,
  tokenProvider: TokenProvider,
  logger: ITelemetryNamespaceLogger,
  tenantId?: string
): Promise<string | undefined> => {
  if (!isOdspResolvedUrl(resolvedUrl)) {
    return undefined;
  }

  return getFileItemLite(
    tokenProvider,
    resolvedUrl.siteUrl,
    resolvedUrl.driveId,
    resolvedUrl.itemId,
    logger,
    tenantId
  ).then((fileItem: FileItemLite | undefined) => {
    // Construct folder url by removing file name from file url
    const filenameIndex: number | undefined = fileItem?.webDavUrl.lastIndexOf('/');

    if (filenameIndex !== undefined && filenameIndex > 0) {
      return fileItem?.webDavUrl.substring(0, filenameIndex);
    }

    return undefined;
  });
};

// TODO: Task #452604: Refine IResolvedUrl types. Types should contain enough information on driver and possible features

/** Assert resolved url is fluid type */
const isFluidResolvedUrl = (resolvedUrl: IResolvedUrl): resolvedUrl is IFluidResolvedUrl => {
  return resolvedUrl.type === 'fluid';
};

/** Assert resolved url is from Odsp driver */
export const isOdspResolvedUrl = (resolvedUrl: IResolvedUrl): resolvedUrl is IOdspResolvedUrl => {
  return (
    isFluidResolvedUrl(resolvedUrl) && 'driveId' in resolvedUrl && 'siteUrl' in resolvedUrl && 'itemId' in resolvedUrl
  );
};
